// ABOUTME: /data-upload — standalone page to upload Tolleson report spreadsheets for ingestion.
// ABOUTME: Auto-detects each file's report type server-side; not wired into the document library.

import { supabase } from "@/contexts/AuthContext";
import { useCustomer } from "@/contexts/useCustomer";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { jwtDecode } from "jwt-decode";
import { useCallback, useRef, useState } from "react";

interface JWTPayload {
  user_role?: string;
}

interface FileResult {
  filename: string;
  detectedSource: string | null;
  detectedLabel: string | null;
  detectedVia: "headers" | "llm" | null;
  rowsParsed: number;
  eventsUpserted: number;
  error?: string;
}

interface IngestResponse {
  results: FileResult[];
}

export const Route = createFileRoute("/_authenticated/data-upload")({
  component: DataUploadPage,
});

function DataUploadPage() {
  const { data: canAccess = false, isLoading: isCheckingRole } = useQuery({
    queryKey: ["data-upload-role-check"],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) return false;
      const decoded = jwtDecode<JWTPayload>(session.access_token);
      return (
        decoded.user_role === "admin" || decoded.user_role === "superadmin"
      );
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (isCheckingRole) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-sm text-muted-foreground">
        You do not have permission to view this page.
      </div>
    );
  }

  return <DataUploadView />;
}

function DataUploadView() {
  const { selectedCustomerId, selectedCustomer } = useCustomer();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [results, setResults] = useState<FileResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addFiles = useCallback((incoming: FileList | null) => {
    if (!incoming) return;
    const accepted = Array.from(incoming).filter((file) =>
      /\.xlsx$/i.test(file.name),
    );
    if (accepted.length === 0) return;
    setResults(null);
    setError(null);
    setFiles((prev) => {
      const byName = new Map(prev.map((file) => [file.name, file]));
      for (const file of accepted) byName.set(file.name, file);
      return Array.from(byName.values());
    });
  }, []);

  const removeFile = useCallback((name: string) => {
    setFiles((prev) => prev.filter((file) => file.name !== name));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedCustomerId || files.length === 0) return;
    setIsUploading(true);
    setError(null);
    setResults(null);
    const uploadRequestId = crypto.randomUUID();

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const formData = new FormData();
      formData.append("customerId", selectedCustomerId);
      for (const file of files) formData.append("files", file);

      const response = await fetch("/api/ingest/spreadsheet", {
        method: "POST",
        // No Content-Type — the browser sets the multipart boundary.
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "X-Upload-Request-Id": uploadRequestId,
        },
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `Upload failed: ${response.status}${text ? ` - ${text}` : ""}`,
        );
      }

      const data = (await response.json()) as IngestResponse;
      setResults(data.results);
      setFiles([]);
    } catch (uploadError) {
      const errorMessage =
        uploadError instanceof Error ? uploadError.message : "Upload failed.";
      const uploadMetadata = {
        uploadRequestId,
        customerId: selectedCustomerId,
        fileCount: files.length,
        totalBytes: files.reduce((sum, file) => sum + file.size, 0),
        filenames: files.map((file) => file.name),
      };

      console.error("[data-upload] upload failed", uploadMetadata, uploadError);
      Sentry.captureException(
        uploadError instanceof Error ? uploadError : new Error(errorMessage),
        {
          tags: { area: "data-upload" },
          extra: uploadMetadata,
        },
      );
      setError(
        errorMessage === "Failed to fetch"
          ? "Upload failed before the server returned a response. Please retry, and contact support if it happens again."
          : errorMessage,
      );
    } finally {
      setIsUploading(false);
    }
  }, [selectedCustomerId, files]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl px-6 py-10">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground">
            Data upload
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload Line TE or State Report spreadsheets. The report type is
            detected automatically.
            {selectedCustomer ? (
              <>
                {" "}
                Ingesting into{" "}
                <span className="font-medium text-foreground">
                  {selectedCustomer.name}
                </span>
                .
              </>
            ) : null}
          </p>
        </header>

        <button
          type="button"
          className="flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-transparent p-8 transition-colors hover:border-primary/50"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            addFiles(e.dataTransfer.files);
          }}
        >
          <span className="text-sm font-medium text-foreground">
            Drop spreadsheets here or click to browse
          </span>
          <span className="text-xs text-muted-foreground">.xlsx</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />

        {files.length > 0 ? (
          <ul className="mt-4 space-y-2">
            {files.map((file) => (
              <li
                key={file.name}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="truncate text-foreground">{file.name}</span>
                <button
                  type="button"
                  className="ml-3 shrink-0 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => removeFile(file.name)}
                  disabled={isUploading}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <button
          type="button"
          className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleUpload}
          disabled={isUploading || files.length === 0 || !selectedCustomerId}
        >
          {isUploading
            ? "Ingesting…"
            : `Ingest ${files.length || ""} file${files.length === 1 ? "" : "s"}`.trim()}
        </button>

        {!selectedCustomerId ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Select a customer first.
          </p>
        ) : null}

        {error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {results ? (
          <section className="mt-6 space-y-2">
            <h2 className="text-sm font-medium text-foreground">Results</h2>
            {results.map((result) => (
              <ResultRow key={result.filename} result={result} />
            ))}
          </section>
        ) : null}
      </div>
    </div>
  );
}

function ResultRow({ result }: { result: FileResult }) {
  const ok = !result.error;
  return (
    <div className="rounded-md border border-border px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="truncate font-medium text-foreground">
          {result.filename}
        </span>
        <span
          className={
            ok
              ? "shrink-0 text-xs font-medium text-green-600"
              : "shrink-0 text-xs font-medium text-destructive"
          }
        >
          {ok ? (result.detectedLabel ?? "Ingested") : "Unrecognized"}
        </span>
      </div>
      {ok ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {result.eventsUpserted} event
          {result.eventsUpserted === 1 ? "" : "s"} ingested from{" "}
          {result.rowsParsed} row{result.rowsParsed === 1 ? "" : "s"}
          {result.detectedVia === "llm" ? " · detected by AI" : null}
        </p>
      ) : (
        <p className="mt-1 text-xs text-destructive">{result.error}</p>
      )}
    </div>
  );
}
